import React, { useMemo } from 'react';
import { useIntl, type IntlShape } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { TypedMemo } from '../../../common/types';
import { outreachResultsPath } from '../outreachPaths';
import type { SongResultsRow } from '../outreachTypes';
import { resultsMessages as messages } from './resultsMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for Outreach; each part uses its own slice of it */
import card from '../live/Campaign.module.scss';
import styles from './Results.module.scss';
/* eslint-enable css-modules/no-unused-class */

const DASH = '—';

/** "Pitching · day 26", "Paused · day 4", "Ended Oct 20" or "Not started". */
export function songStatusLine(intl: IntlShape, row: SongResultsRow): string {
  if (row.status === 'done') {
    return row.doneAt === null
      ? intl.formatMessage(messages.statusEndedPlain)
      : intl.formatMessage(messages.statusEnded, {
          date: intl.formatDate(new Date(row.doneAt), { month: 'short', day: 'numeric' }),
        });
  }
  if (row.startedAt === null) {
    return intl.formatMessage(messages.statusNotStarted);
  }
  return intl.formatMessage(row.status === 'paused' ? messages.statusPaused : messages.statusPitching, {
    day: row.dayNumber,
  });
}

/**
 * Every song side by side (design outreach-v2 n2): pitched, listened, replied, adds and reach of
 * each campaign, the newest first. A row opens the Results tab of its campaign.
 */
const ResultsCompareComponent: React.FC<{ rows: SongResultsRow[] }> = ({ rows }) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const items = useMemo(
    () =>
      rows.map((row) => {
        const percent = (value: number | null) => (value === null ? DASH : `${value}%`);
        return (
          <button
            key={row.campaignUuid}
            type="button"
            className={`${styles.tableRow} ${styles.compareRow}`}
            // eslint-disable-next-line no-void
            onClick={() => void navigate(outreachResultsPath(row.campaignUuid))}
            data-testid="results-compare-row"
          >
            {row.coverUrl === null ? (
              <span className={styles.cover} />
            ) : (
              <img className={styles.cover} src={row.coverUrl} alt="" />
            )}
            <span className={styles.name}>
              <b>{row.songTitle}</b>
              <small>{songStatusLine(intl, row)}</small>
            </span>
            <span className={styles.num}>{intl.formatNumber(row.pitched)}</span>
            <span className={`${styles.num} ${styles.wide}`}>{percent(row.listenedPercent)}</span>
            <span className={`${styles.num} ${styles.wide}`}>{percent(row.repliedPercent)}</span>
            <span className={`${styles.num} ${styles.wide}`}>
              {row.adds === 0 ? DASH : intl.formatNumber(row.adds)}
            </span>
            <span className={`${styles.num} ${styles.wide}`}>
              {row.reach === 0 ? DASH : intl.formatNumber(row.reach)}
            </span>
          </button>
        );
      }),
    [intl, navigate, rows]
  );
  return (
    <section className={`${card.card} ${card.cardFlush}`} data-testid="results-compare">
      <div className={`${styles.tableRow} ${styles.compareRow} ${styles.tableHead}`} aria-hidden>
        <span />
        <span>{intl.formatMessage(messages.colSong)}</span>
        <span className={styles.num}>{intl.formatMessage(messages.colPitched)}</span>
        <span className={`${styles.num} ${styles.wide}`}>{intl.formatMessage(messages.colListened)}</span>
        <span className={`${styles.num} ${styles.wide}`}>{intl.formatMessage(messages.colReplied)}</span>
        <span className={`${styles.num} ${styles.wide}`}>{intl.formatMessage(messages.colAdds)}</span>
        <span className={`${styles.num} ${styles.wide}`}>{intl.formatMessage(messages.colReach)}</span>
      </div>
      {items}
    </section>
  );
};

export const ResultsCompare = TypedMemo(ResultsCompareComponent);
